/**
 * Utility functions to generate valid parcel numbers for testing purposes
 * Based on the provided regex patterns for each carrier
 */

// Helper function to generate random alphanumeric characters
const getRandomAlphanumeric = (length: number): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
  let result = ""
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// Helper function to generate random digits
const getRandomDigits = (length: number): string => {
  const digits = "0123456789"
  let result = ""
  for (let i = 0; i < length; i++) {
    result += digits.charAt(Math.floor(Math.random() * digits.length))
  }
  return result
}

export const generateColissimoParcel = (): string => {
  // Format: must start with 6M and random characters. Example: 6M21899730854
  return "6M" + getRandomDigits(11)
}

export const generateGLSParcel = (): string => {
  // Format: must start with 00, then 6 characters. Example: 00K11LWF
  return "00" + getRandomAlphanumeric(6)
}

export const generateDHLParcel = (): string => {
  // Format: must start with JD, example: JD014600012292176008
  return "JD" + getRandomDigits(18)
}

export const generateGeodisParcel = (): string => {
  // Format: must start with JVGT. Example: JVGTC1597870000597366
  return "JVGT" + getRandomAlphanumeric(16)
}

export const generateRelaisColisParcel = (): string => {
  // Format: must start with 9F, example: 9F251500053301
  return "9F" + getRandomDigits(12)
}

export const generateUPSParcel = (): string => {
  // Format: must start with 1Z. Example: 1ZH9V4556870866137
  return "1Z" + getRandomAlphanumeric(16)
}

// Helper function to get admin user email alias
const getAdminEmailAlias = (): string => {
  try {
    // Try to get token from localStorage
    const token = window.localStorage.getItem("API_TOKEN")

    if (token) {
      // Decode JWT token to get user email
      const base64Url = token.split(".")[1]
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
          })
          .join(""),
      )

      const decoded = JSON.parse(jsonPayload)

      if (decoded.email) {
        // Extract the local part before @ and add +desti
        const emailParts = decoded.email.split("@")
        if (emailParts.length === 2) {
          return `${emailParts[0]}+desti@${emailParts[1]}`
        }
      }
    }
  } catch (error) {
    console.warn("Could not decode token for email alias:", error)
  }

  // Fallback to default admin email structure
  return "admin+desti@mypickme.com"
}

// Test data generators for each carrier
export const carrierTestData = {
  colissimo: {
    name: "Colissimo",
    generate: generateColissimoParcel,
    sampleData: {
      parcelNo: "",
      shipmentNo: "", // Will be set dynamically with parcelNo + "-S"
      shipperName: "Test Colissimo Shipper",
      recipient: {
        firstName: "Prenom desti",
        lastName: "Nom desti",
        email: getAdminEmailAlias(),
        phone: null,
      },
    },
  },
  gls: {
    name: "GLS",
    generate: generateGLSParcel,
    sampleData: {
      parcelNo: "",
      shipmentNo: "", // Will be set dynamically with parcelNo + "-S"
      shipperName: "Test GLS Shipper",
      recipient: {
        firstName: "Prenom desti",
        lastName: "Nom desti",
        email: getAdminEmailAlias(),
        phone: null,
      },
    },
  },
  dhl: {
    name: "DHL",
    generate: generateDHLParcel,
    sampleData: {
      parcelNo: "",
      shipmentNo: "", // Will be set dynamically with parcelNo + "-S"
      shipperName: "Test DHL Shipper",
      recipient: {
        firstName: "Prenom desti",
        lastName: "Nom desti",
        email: getAdminEmailAlias(),
        phone: null,
      },
    },
  },
  geodis: {
    name: "Geodis",
    generate: generateGeodisParcel,
    sampleData: {
      parcelNo: "",
      shipmentNo: "", // Will be set dynamically with parcelNo + "-S"
      shipperName: "Test Geodis Shipper",
      recipient: {
        firstName: "Prenom desti",
        lastName: "Nom desti",
        email: getAdminEmailAlias(),
        phone: null,
      },
    },
  },
  relaiscolis: {
    name: "Relais Colis",
    generate: () => generateRelaisColisParcel(),
    sampleData: {
      parcelNo: "",
      shipmentNo: "", // Will be set dynamically with parcelNo + "-S"
      shipperName: "Test Relais Colis Shipper",
      recipient: {
        firstName: "Prenom desti",
        lastName: "Nom desti",
        email: getAdminEmailAlias(),
        phone: null,
      },
    },
  },
  ups: {
    name: "UPS",
    generate: generateUPSParcel,
    sampleData: {
      parcelNo: "",
      shipmentNo: "", // Will be set dynamically with parcelNo + "-S"
      shipperName: "Test UPS Shipper",
      recipient: {
        firstName: "Prenom desti",
        lastName: "Nom desti",
        email: getAdminEmailAlias(),
        phone: null,
      },
    },
  },
}
